import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, LogOut, Users, UserCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useAppSelector } from '@/stores/hooks';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from './ui/sidebar';

interface MenuItem {
  label: string;
  path: string;
  icon: React.ReactElement;
}

interface MenuGroup {
  groupLabel: string;
  items: MenuItem[];
}

export const AppSidebar: React.FC = () => {
  const location = useLocation();
  const { userProfile } = useAppSelector(state => state.auth);
  const { isMobile, setOpenMobile } = useSidebar();

  // Define menu structure
  const menuGroups: MenuGroup[] = [
    {
      groupLabel: 'Main',
      items: [
        { label: 'Home', path: '/', icon: <Home /> },
        { label: 'Advisers', path: '/advisers', icon: <Users /> },
        { label: 'Clients', path: '/clients', icon: <UserCircle /> },
      ]
    },
  ];

  // Handle link clicks (close mobile sidebar)
  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Handle sign out
  const handleSignOut = () => {
    window.location.href = '/.auth/logout';
  };

  const isCurrentPath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Sidebar variant="inset" side="right">
      <SidebarHeader>
        <h2 className="px-2 text-lg font-semibold tracking-tight">
          Admin Portal
        </h2>
      </SidebarHeader>

      <SidebarContent>
        {menuGroups.map((group, index) => (
          <SidebarGroup key={index}>
            <SidebarGroupLabel>{group.groupLabel}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      asChild
                      isActive={isCurrentPath(item.path)}
                      onClick={handleLinkClick}
                    >
                      <Link to={item.path}>
                        {item.icon}
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        {userProfile && (
          <div className="px-2 py-2">
            <p className="text-sm font-medium">{userProfile.name}</p>
            <p className="text-xs text-muted-foreground">{userProfile.name}</p>
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 justify-start"
              onClick={handleSignOut}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>
        )}
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
};
